/**
 * Global Wishlist System
 * Handles adding/removing products from wishlist with toast notifications
 */

(function() {
  'use strict';

  // Configuration
  const API_BASE = 'https://app.bepivotal.co.uk/api/v1';
  const STOREFRONT_KEY = '7a2918b913ae5bf9e75103ea994aba1df813abd1b185354f3b69304acfd4a17d';
  const SESSION_TOKEN_KEY = 'wishlist_session_token';

  // State
  let wishlistState = {
    isLoggedIn: false,
    customerId: null,
    sessionToken: null,
    rooms: [],
    isInitialized: false
  };

  // ============================================
  // Session Management
  // ============================================

  function getSessionToken() {
    let token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (!token) {
      token = generateUUID();
      localStorage.setItem(SESSION_TOKEN_KEY, token);
    }
    return token;
  }

  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function checkAuthStatus() {
    if (window.Shopify && window.Shopify.customer) {
      wishlistState.isLoggedIn = true;
      wishlistState.customerId = window.Shopify.customer.id;
    } else {
      wishlistState.isLoggedIn = false;
      wishlistState.customerId = null;
      wishlistState.sessionToken = getSessionToken();
    }
  }

  // ============================================
  // API Calls
  // ============================================

  async function apiRequest(endpoint, method = 'GET', body = null) {
    const options = {
      method,
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'X-Storefront-Key': STOREFRONT_KEY
      }
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, options);
      
      // Check if response was redirected (endpoint doesn't exist)
      if (response.redirected) {
        throw new Error('Wishlist API endpoint not found - backend not implemented yet');
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        const error = new Error(`API Error ${response.status}: ${errorText}`);
        error.status = response.status;
        error.statusText = errorText;
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('Wishlist API Request failed:', error);
      throw error;
    }
  }

  async function fetchRooms() {
    let endpoint = '/wishlist';
    if (!wishlistState.isLoggedIn && wishlistState.sessionToken) {
      endpoint += `?session_token=${wishlistState.sessionToken}`;
    }
    
    const data = await apiRequest(endpoint, 'GET');
    
    if (data.session_token && !wishlistState.isLoggedIn) {
      wishlistState.sessionToken = data.session_token;
      localStorage.setItem(SESSION_TOKEN_KEY, data.session_token);
    }

    wishlistState.rooms = data.rooms || [];
    return wishlistState.rooms;
  }

  async function addToWishlist(productId, variantId = null) {
    const payload = {
      is_logged_in: wishlistState.isLoggedIn,
      customer_id: wishlistState.customerId,
      session_token: wishlistState.sessionToken,
      product_id: productId,
      variant_id: variantId
    };

    const data = await apiRequest('/wishlist/add', 'POST', payload);
    return data;
  }

  async function removeFromWishlist(productId, roomId = null) {
    const payload = {
      is_logged_in: wishlistState.isLoggedIn,
      customer_id: wishlistState.customerId,
      session_token: wishlistState.sessionToken,
      product_id: productId,
      room_id: roomId
    };

    await apiRequest('/wishlist/remove', 'POST', payload);
  }

  // ============================================
  // Toast Notification System
  // ============================================

  function createToastContainer() {
    let container = document.getElementById('wishlist-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'wishlist-toast-container';
      container.className = 'wishlist-toast-container';
      document.body.appendChild(container);
    }
    return container;
  }

  function showToast(product, action = 'added') {
    const container = createToastContainer();
    
    const toast = document.createElement('div');
    toast.className = 'wishlist-toast wishlist-toast-enter';
    
    const productImage = product.image || product.featured_image || '';
    const productTitle = product.title || '';
    
    toast.innerHTML = `
      <div class="wishlist-toast-content">
        ${productImage ? `<img src="${productImage}" alt="${productTitle}" class="wishlist-toast-image">` : ''}
        <div class="wishlist-toast-text">
          <div class="wishlist-toast-title">Item ${action} to wish list</div>
          ${productTitle ? `<div class="wishlist-toast-product">${productTitle}</div>` : ''}
        </div>
      </div>
      <a href="/pages/wishlist" class="wishlist-toast-action">
        View Wishlist →
      </a>
    `;
    
    container.appendChild(toast);
    
    // Trigger animation
    setTimeout(() => toast.classList.add('wishlist-toast-show'), 10);
    
    // Auto-remove after 8 seconds (longer duration)
    setTimeout(() => removeToast(toast), 8000);
  }

  function removeToast(toast) {
    toast.classList.remove('wishlist-toast-show');
    toast.classList.add('wishlist-toast-exit');
    setTimeout(() => toast.remove(), 300);
  }

  // ============================================
  // Room Selector Modal
  // ============================================

  function showRoomSelector(product) {
    // Filter out default room if there are other rooms
    const selectableRooms = wishlistState.rooms.filter(r => !r.is_default || wishlistState.rooms.length === 1);
    
    const modal = document.createElement('div');
    modal.className = 'wishlist-room-modal';
    modal.innerHTML = `
      <div class="wishlist-room-overlay"></div>
      <div class="wishlist-room-content">
        <div class="wishlist-room-header">
          <h3>Add to Collection</h3>
          <button class="wishlist-room-close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="wishlist-room-body">
          <div class="wishlist-room-product">
            ${product.image ? `<img src="${product.image}" alt="${product.title}">` : ''}
            <span>${product.title}</span>
          </div>
          <div class="wishlist-room-list">
            ${selectableRooms.map(room => `
              <button class="wishlist-room-option" data-room-id="${room.id}">
                <span>${room.name}</span>
                <span class="wishlist-room-count">${room.items_count || 0} items</span>
              </button>
            `).join('')}
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    setTimeout(() => modal.classList.add('show'), 10);
    
    // Handle room selection
    modal.querySelectorAll('.wishlist-room-option').forEach(btn => {
      btn.addEventListener('click', async () => {
        const roomId = parseInt(btn.dataset.roomId);
        try {
          await moveProductToRoom(product.id, roomId);
          closeModal(modal);
          showSuccessToast(`Moved to ${btn.querySelector('span').textContent}`);
        } catch (error) {
          console.error('Failed to move product:', error);
        }
      });
    });
    
    // Handle close
    modal.querySelector('.wishlist-room-close').addEventListener('click', () => closeModal(modal));
    modal.querySelector('.wishlist-room-overlay').addEventListener('click', () => closeModal(modal));
  }

  function closeModal(modal) {
    modal.classList.remove('show');
    setTimeout(() => modal.remove(), 300);
  }

  async function moveProductToRoom(productId, toRoomId) {
    const payload = {
      is_logged_in: wishlistState.isLoggedIn,
      customer_id: wishlistState.customerId,
      session_token: wishlistState.sessionToken,
      product_id: productId,
      to_room_id: toRoomId
    };

    await apiRequest('/wishlist/move', 'POST', payload);
  }

  function showSuccessToast(message) {
    const container = createToastContainer();
    const toast = document.createElement('div');
    toast.className = 'wishlist-toast wishlist-toast-enter wishlist-toast-success';
    toast.innerHTML = `
      <div class="wishlist-toast-content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        <div class="wishlist-toast-text">
          <div class="wishlist-toast-title">${message}</div>
        </div>
      </div>
    `;
    
    container.appendChild(toast);
    setTimeout(() => toast.classList.add('wishlist-toast-show'), 10);
    setTimeout(() => removeToast(toast), 3000);
  }

  function showErrorToast(message) {
    const container = createToastContainer();
    const toast = document.createElement('div');
    toast.className = 'wishlist-toast wishlist-toast-enter wishlist-toast-error';
    toast.innerHTML = `
      <div class="wishlist-toast-content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <div class="wishlist-toast-text">
          <div class="wishlist-toast-title">${message}</div>
        </div>
      </div>
    `;
    
    container.appendChild(toast);
    setTimeout(() => toast.classList.add('wishlist-toast-show'), 10);
    setTimeout(() => removeToast(toast), 4000);
  }

  function showAlreadyInWishlistToast(productName) {
    const container = createToastContainer();
    const toast = document.createElement('div');
    toast.className = 'wishlist-toast wishlist-toast-enter wishlist-toast-info';
    toast.innerHTML = `
      <div class="wishlist-toast-content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        <div class="wishlist-toast-text">
          <div class="wishlist-toast-title">${productName} is already in your wishlist</div>
        </div>
      </div>
    `;
    
    container.appendChild(toast);
    setTimeout(() => toast.classList.add('wishlist-toast-show'), 10);
    setTimeout(() => removeToast(toast), 4000);
  }

  // ============================================
  // Public API
  // ============================================

  window.WishlistGlobal = {
    async init() {
      if (wishlistState.isInitialized) return;
      
      checkAuthStatus();
      
      try {
        await fetchRooms();
        wishlistState.isInitialized = true;
      } catch (error) {
        console.error('Failed to initialize wishlist:', error);
      }
    },

    async addProduct(productId, variantId, productData) {
      try {
        // Just check auth status, don't fetch rooms (not needed for adding)
        if (!wishlistState.isInitialized) {
          checkAuthStatus();
          wishlistState.isInitialized = true;
        }

        await addToWishlist(productId, variantId);
        
        // Refresh wishlist data to get updated state
        await fetchRooms();
        
        showToast(productData, 'added');
        
        // Trigger custom event for other components
        window.dispatchEvent(new CustomEvent('wishlist:added', { 
          detail: { productId, variantId, productData }
        }));
        
        return true;
      } catch (error) {
        console.error('Failed to add to wishlist:', error);
        
        // Show user-friendly error message
        if (error.message.includes('backend not implemented')) {
          showErrorToast('Wishlist feature coming soon! The backend API is not ready yet.');
        } else if ((error.status === 409) || 
                   error.message.includes('409') || 
                   (error.statusText && error.statusText.toLowerCase().includes('already')) || 
                   error.message.toLowerCase().includes('already')) {
          const productName = productData?.title || 'This item';
          showAlreadyInWishlistToast(productName);
        } else {
          showErrorToast('Failed to add to wishlist. Please try again.');
        }
        
        return false;
      }
    },

    async removeProduct(productId, roomId) {
      try {
        await removeFromWishlist(productId, roomId);
        
        // Refresh wishlist data to get updated state
        await fetchRooms();
        
        window.dispatchEvent(new CustomEvent('wishlist:removed', { 
          detail: { productId, roomId }
        }));
        
        return true;
      } catch (error) {
        console.error('Failed to remove from wishlist:', error);
        return false;
      }
    },

    getRooms() {
      return wishlistState.rooms;
    },

    isLoggedIn() {
      return wishlistState.isLoggedIn;
    }
  };

  // Don't auto-initialize - only init when needed (lazy loading)
  // This prevents unnecessary API calls on every page load
  // Init will happen automatically when user first interacts with wishlist

  // Track which buttons already have observers to prevent duplicates
  const protectedButtons = new WeakSet();
  
  // Protect custom wishlist buttons from Swym interference
  function protectCustomButtons() {
    const customButtons = document.querySelectorAll('[data-custom-wishlist="true"]');
    customButtons.forEach(button => {
      // Skip if already protected
      if (protectedButtons.has(button)) {
        return;
      }
      
      // Mark as protected
      protectedButtons.add(button);
      
      // Force visibility
      button.style.display = 'block';
      button.style.visibility = 'visible';
      button.style.opacity = '1';
      
      // Prevent Swym from adding its classes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            // Remove any Swym classes that might hide the button
            button.classList.remove('swym-button', 'swym-add-to-wishlist-view-product');
          }
        });
      });
      
      observer.observe(button, { attributes: true });
    });
  }

  // Global function for onclick handlers (non-blocking)
  window.addToWishlist = function(event, productId, variantId, productTitle, productImage, productHandle) {
    // Stop event from bubbling to parent elements
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    
    console.log('Adding to wishlist:', productId, productTitle);
    
    const productData = {
      id: productId,
      title: productTitle,
      image: productImage,
      handle: productHandle
    };
    
    // Run async operation without blocking
    if (window.WishlistGlobal) {
      // Don't await - let it run in background
      Promise.resolve().then(() => {
        return window.WishlistGlobal.addProduct(productId, variantId, productData);
      }).then((success) => {
        // Button state will be updated automatically by updateWishlistButtonStates()
        // which is called after fetchRooms() in addProduct()
        if (!success) {
          console.warn('Failed to add product to wishlist');
        }
      }).catch(err => {
        console.error('Failed to add to wishlist:', err);
      });
    } else {
      console.error('WishlistGlobal not loaded');
    }
    
    return false;
  };

  // Run protection on load and when new products are added
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', protectCustomButtons);
  } else {
    protectCustomButtons();
  }
  
  // Re-run protection when DOM changes (for dynamically loaded products)
  // Only protect buttons, don't update states (that's handled per-page)
  let protectionTimer = null;
  const bodyObserver = new MutationObserver(() => {
    // Clear existing timer
    if (protectionTimer) {
      clearTimeout(protectionTimer);
    }
    
    // Debounce the protection call
    protectionTimer = setTimeout(() => {
      protectCustomButtons();
      protectionTimer = null;
    }, 200); // 200ms debounce
  });
  
  if (document.body) {
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

})();
